import { Typography, useMediaQuery } from "@mui/material";
import PageWrapper from "components/PageWrapper";
import { useLogEventOnLoad } from "hooks/useLogEventOnLoad";
import { useState } from "react";
import { StringParam, useQueryParams } from "use-query-params";
import { CustomArrayParam, StatusParam } from "./filters/customQueryParamDefinitions";
import { InterestGroupFilter } from "./InterestGroupFilter";
import { InterestGroupList } from "./InterestGroupList";
import {
    getCurrentCohort,
    isUserInFreeTrialSession,
    isUserInSetupFlow,
} from "features/signup/signupHelpers";
import makeStyles from "@mui/styles/makeStyles";
import { SetupProgressBar } from "features/setup/SetupProgressBar";
import { useLocation } from "react-router";
import { SELECTED_INTEREST_GROUP_FIELD } from "./constants";
import { useSelector } from "react-redux";
import { selectUser } from "features/auth/auth";
import { ProductFeature } from "@app/shared/types";
import { DEFAULT_FREE_TRIAL_DAYS } from "@app/shared/constants";

const useStyles = makeStyles((theme) => {
    return {
        heading: {
            marginBottom: 0,
            [theme.breakpoints.down("lg")]: {
                display: "none",
            },
        },
        description: {},
        subtitle: {
            [theme.breakpoints.down("lg")]: {
                maxWidth: "70%",
                marginBottom: theme.spacing(2),
            },
        },
    };
});

export const InterestGroupListPage = () => {
    const [query, setQuery] = useQueryParams({
        topic: CustomArrayParam,
        timeOfDay: CustomArrayParam,
        dayOfWeek: CustomArrayParam,
        status: StatusParam,
        partner: StringParam,
    });

    const classes = useStyles();

    const [groupCount, setGroupCount] = useState(0);

    const user = useSelector(selectUser);
    const isActiveMember = user?.features.includes(ProductFeature.CORE_MEMBERSHIP);

    const location = useLocation();
    const isSetupFlow = isUserInSetupFlow(location);

    const isFreeTrialSignupFlow = isUserInFreeTrialSession() && (!isActiveMember || isSetupFlow);

    const preselectedInterestGroup = sessionStorage.getItem(SELECTED_INTEREST_GROUP_FIELD);

    useLogEventOnLoad("View Groups", {
        isFreeTrial: isFreeTrialSignupFlow,
        cohort: getCurrentCohort(),
    });

    const resetFilters = () => {
        setQuery({
            topic: undefined,
            timeOfDay: undefined,
            dayOfWeek: undefined,
            status: undefined,
        });
    };

    const getSubtitle = () => {
        if (isFreeTrialSignupFlow) {
            return `Join as many groups as you like during your ${DEFAULT_FREE_TRIAL_DAYS}-day free trial. Choose your first group to get started.`;
        } else {
            return "Join as many groups as you like. Choose your first group to get started.";
        }
    };

    return (
        <>
            {isSetupFlow && <SetupProgressBar currentStep={4} />}
            <PageWrapper>
                <Typography variant="h2" className={classes.heading}>
                    What are Mindful Friends Groups?
                </Typography>
                <Typography variant="body1" className={classes.description}>
                    Mindful Friends Groups meet weekly online and are guided by highly-trained
                    teachers. They offer spaces for reflection and connection. Sessions include
                    meditation, practice in intimate breakouts, and engaging group sharing.
                </Typography>
                <Typography variant="h2" className={classes.heading}>
                    Choose your first group
                </Typography>
                <Typography variant="body1" className={classes.subtitle}>
                    {getSubtitle()}
                </Typography>

                <InterestGroupFilter
                    filters={query}
                    setFilters={setQuery}
                    groupCount={groupCount}
                    resetFilters={resetFilters}
                />
                <InterestGroupList
                    filters={query}
                    resetFilters={resetFilters}
                    setGroupCount={setGroupCount}
                />
            </PageWrapper>
        </>
    );
};
